import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  createTranslator,
  dir as directionOf,
  resolveLocale,
  type Locale,
  type TranslateParams,
  type TranslationKey,
} from './index.js';
import { getTelegramLanguage } from '../telegram/webapp.js';
import { prefs } from '../lib/storage.js';

export interface I18nValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: (key: TranslationKey, params?: TranslateParams) => string;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nValue | null>(null);
const LOCALE_PREF = 'locale';

export function initialLocale(): Locale {
  const stored = prefs.get(LOCALE_PREF);
  return stored ? resolveLocale(stored) : resolveLocale(getTelegramLanguage());
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionOf(locale);
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      dir: directionOf(locale),
      t: createTranslator(locale),
      setLocale: (next) => {
        prefs.set(LOCALE_PREF, next);
        setLocaleState(next);
      },
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
